import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { ActivityState, ActivityType } from '@/api/models/activity'
import { type LearnerObjectiveAccessFullModel, ObjectiveWorkflowState } from '@/api/models/aggregate'
import { Attention } from '@/components/Attention/Attention'
import { useCourseNavigate } from '@/hooks/useCourseNavigate'
import { useEventLogger } from '@/hooks/useEventLogger'
import { useIsActivitySupportsMobileReview } from '@/hooks/useIsActivitySupportsMobileReview'
import { useIsMobile } from '@/hooks/useIsMobile'
import { useAppDispatch } from '@/store/hooks'
import { setLastPageURL } from '@/store/slices/layoutSlice'
import { ContinueButton } from '@/views/courses/CourseInfo/components/Buttons/ContinueButton'
import { useIsMigratedCourse } from '@/views/courses/CourseInfo/components/EnrolledCourseButtons/useIsMigratedCourse'
import { useProctoringSessionFinished } from '@/views/courses/CourseInfo/hooks/useProctoringSessionFinished'
import styles from './EnrolledCourseButtons.module.scss'
import { RetakeButton } from '../Buttons/RetakeButton'
import { ReviewButton } from '../Buttons/ReviewButton'
import { ShareButton } from '../Buttons/ShareButton/ShareButton'
import { StartButton } from '../Buttons/StartButton'

type EnrolledCourseButtonsProps = {
  aggregate: LearnerObjectiveAccessFullModel
  startWorkflow: (isRetake?: boolean) => Promise<unknown>
  hasResults: boolean
  supportReview?: boolean
  disabled: boolean
  isWorkflowStarting: boolean
  canReview?: boolean
}

export const EnrolledCourseButtons: React.FC<EnrolledCourseButtonsProps> = ({
  aggregate,
  startWorkflow,
  hasResults,
  supportReview,
  disabled,
  isWorkflowStarting,
  canReview,
}) => {
  const { t } = useTranslation()
  const { logEvent } = useEventLogger()

  const { search, pathname } = useLocation()
  const { navigateToContent } = useCourseNavigate()

  const dispatch = useAppDispatch()

  const isMobile = useIsMobile()

  const [isCourseReviewDisabled, isProctoringSessionLoading] = useProctoringSessionFinished()

  const objective = useMemo(() => aggregate?.objective, [aggregate])

  const isDraft = objective?.activity?.state === ActivityState.draft
  const isArchived = objective?.activity?.state === ActivityState.archived
  const isStarted =
    aggregate?.objectiveWorkflowAggregate?.lastObjectiveWorkflow?.state === ObjectiveWorkflowState.inProgress
  const isDraftOrArchived = isDraft || isArchived

  const isLastWorkflowFinished =
    aggregate?.objectiveWorkflowAggregate?.lastObjectiveWorkflow?.state === ObjectiveWorkflowState.finished
  const canBeRetaken = aggregate?.retake
  const isLti = objective?.activity?.type === ActivityType.lti
  const isReadyOrExpired =
    objective?.activity?.state === ActivityState.ready || objective?.activity?.state === ActivityState.expired

  const isEdxMigratedCourse = useIsMigratedCourse(aggregate.objectiveWorkflowAggregate?.lastObjectiveRecord)

  const showMigratedCourseWarning = useMemo(() => {
    return isEdxMigratedCourse && !isStarted
  }, [isEdxMigratedCourse, isStarted])

  const showStartCourseBtn = useMemo(() => {
    return (
      !isEdxMigratedCourse &&
      (!aggregate?.objectiveWorkflowAggregate?.lastObjectiveWorkflow ||
        (aggregate?.objectiveWorkflowAggregate?.lastObjectiveWorkflow && isWorkflowStarting) ||
        (isLastWorkflowFinished &&
          canBeRetaken &&
          objective?.activity?.type !== ActivityType.text &&
          !(isLti && isReadyOrExpired)))
    )
  }, [
    aggregate?.objectiveWorkflowAggregate?.lastObjectiveWorkflow,
    objective?.activity?.type,
    isWorkflowStarting,
    isLti,
    isEdxMigratedCourse,
    isLastWorkflowFinished,
    isReadyOrExpired,
    canBeRetaken,
  ])

  const showRetakeCourseBtn = useMemo(() => {
    return showMigratedCourseWarning || (isLastWorkflowFinished && canBeRetaken && isLti && isReadyOrExpired)
  }, [showMigratedCourseWarning, isLastWorkflowFinished, canBeRetaken, isLti, isReadyOrExpired])

  const showContinueCourseBtn = useMemo(() => {
    return (
      !showStartCourseBtn &&
      aggregate?.objectiveWorkflowAggregate?.lastObjectiveWorkflow?.state === ObjectiveWorkflowState.inProgress &&
      !isCourseReviewDisabled &&
      !isWorkflowStarting
    )
  }, [
    aggregate?.objectiveWorkflowAggregate?.lastObjectiveWorkflow?.state,
    isWorkflowStarting,
    showStartCourseBtn,
    isCourseReviewDisabled,
  ])

  const showReviewCourseBtn = useMemo(() => {
    return (
      aggregate?.objectiveWorkflowAggregate?.lastObjectiveWorkflow?.state !== ObjectiveWorkflowState.inProgress &&
      !isCourseReviewDisabled &&
      supportReview &&
      (isLastWorkflowFinished
        ? !aggregate?.objectiveWorkflowAggregate?.lastObjectiveRecord?.protectResults
        : !aggregate?.objectiveWorkflowAggregate?.lastObjectiveWorkflow?.protectResults)
    )
  }, [
    aggregate?.objectiveWorkflowAggregate?.lastObjectiveWorkflow?.state,
    aggregate?.objectiveWorkflowAggregate?.lastObjectiveRecord?.protectResults,
    aggregate?.objectiveWorkflowAggregate?.lastObjectiveWorkflow?.protectResults,
    supportReview,
    isCourseReviewDisabled,
    isLastWorkflowFinished,
  ])

  const onClickContinue = useCallback(() => {
    navigateToContent(objective?.id)
  }, [navigateToContent, objective])

  const onClickReview = useCallback(() => {
    navigateToContent(objective?.id, true)
    logEvent('course_open_for_review', { objective })
    dispatch(setLastPageURL(pathname + search))
  }, [dispatch, logEvent, navigateToContent, objective, pathname, search])

  const activitySupportsMobileReview = useIsActivitySupportsMobileReview()
  const notSupportsMobileView = isMobile && !activitySupportsMobileReview

  const isDraftOrArchivedAndNotStarted = isDraftOrArchived && !isStarted

  const showStartButtonCondition = useMemo(() => {
    return showStartCourseBtn && !hasResults && !isDraftOrArchivedAndNotStarted
  }, [showStartCourseBtn, hasResults, isDraftOrArchivedAndNotStarted])

  const startButton = useCallback(() => {
    if (showStartButtonCondition) {
      return <StartButton onClick={() => startWorkflow()} loading={isWorkflowStarting} disabled={disabled} />
    }

    return null
  }, [showStartButtonCondition, isWorkflowStarting, disabled, startWorkflow])

  const retakeButton = useCallback(() => {
    if (showRetakeCourseBtn) {
      return <RetakeButton onClick={() => startWorkflow(true)} loading={isWorkflowStarting} disabled={disabled} />
    }

    return null
  }, [showRetakeCourseBtn, isWorkflowStarting, disabled, startWorkflow])

  const reviewButton = useCallback(() => {
    if (showReviewCourseBtn) {
      return (
        <ReviewButton
          onClick={onClickReview}
          loading={isProctoringSessionLoading}
          disabled={!canReview || isEdxMigratedCourse}
        />
      )
    }

    return null
  }, [showReviewCourseBtn, isProctoringSessionLoading, isEdxMigratedCourse, canReview, onClickReview])

  const continueButton = useCallback(() => {
    if (showContinueCourseBtn) {
      return (
        <ContinueButton
          onClick={onClickContinue}
          loading={isProctoringSessionLoading}
          disabled={disabled && !canReview}
        />
      )
    }

    return null
  }, [showContinueCourseBtn, isProctoringSessionLoading, disabled, canReview, onClickContinue])

  const shareButton = useCallback(() => {
    return <ShareButton />
  }, [])

  if (notSupportsMobileView) {
    return <Attention message={t('mobileAttention')} />
  }

  if (!aggregate || !objective) {
    return null
  }

  return (
    <>
      <div className={styles.buttons}>
        {reviewButton()}
        {startButton()}
        {retakeButton()}
        {continueButton()}
        {shareButton()}
      </div>

      {showMigratedCourseWarning && <Attention message={t('edxMigrate.noReviewRetake')} />}
    </>
  )
}
